import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const TakeSurvey = () => {
  const location = useLocation();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [rewrittenTexts, setRewrittenTexts] = useState({});
  const [originalTexts, setOriginalTexts] = useState({});
  const [rewritingQuestions, setRewritingQuestions] = useState({});
  const [selectedVersions, setSelectedVersions] = useState({});
  const [surveyStartTime, setSurveyStartTime] = useState(null);
  const [questionStartTimes, setQuestionStartTimes] = useState({});
  const [questionTimes, setQuestionTimes] = useState({});
  const [hastyQuestions, setHastyQuestions] = useState([]);
  const [idleQuestions, setIdleQuestions] = useState([]);
  const [surveySettings, setSurveySettings] = useState({
    hasty_question_threshold: 1.0,
    idle_question_threshold: 200.0,
    hasty_survey_threshold: 2.0
  });

  useEffect(() => {
    loadSurvey();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/survey-settings/public`);
      setSurveySettings(response.data);
    } catch (error) {
      console.error('Failed to load settings, using defaults');
    }
  };

  const loadSurvey = async () => {
    try {
      const id = location.pathname.split('/')[2];
      const response = await axios.get(`${API}/surveys/${id}/public`);
      setSurvey(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setSurvey({ error: 'inactive' });
      } else {
        toast.error('Survey not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const startQuestionTimer = (questionId) => {
    if (!surveyStartTime) setSurveyStartTime(Date.now());
    if (!questionStartTimes[questionId]) {
      setQuestionStartTimes({...questionStartTimes, [questionId]: Date.now()});
    }
  };

  const recordQuestionTime = (questionId) => {
    if (questionStartTimes[questionId]) {
      const timeSpent = (Date.now() - questionStartTimes[questionId]) / 1000;
      setQuestionTimes({...questionTimes, [questionId]: timeSpent});
      if (timeSpent < surveySettings.hasty_question_threshold && !hastyQuestions.includes(questionId)) {
        setHastyQuestions([...hastyQuestions, questionId]);
      }
      if (timeSpent > surveySettings.idle_question_threshold && !idleQuestions.includes(questionId)) {
        setIdleQuestions([...idleQuestions, questionId]);
      }
    }
  };

  const handleAnswerChange = (questionId, value) => {
    startQuestionTimer(questionId);
    setAnswers({...answers, [questionId]: value});
    if (value) recordQuestionTime(questionId);
  };

  const handleRewriteText = async (questionId, currentText) => {
    if (!currentText || currentText.trim().length < 3) {
      toast.error('Please write at least a few words before rewriting');
      return;
    }
    setRewritingQuestions({...rewritingQuestions, [questionId]: true});
    try {
      const surveyId = location.pathname.split('/')[2];
      const response = await axios.post(`${API}/ai/rewrite-text`, { text: currentText, survey_id: surveyId });
      setOriginalTexts({...originalTexts, [questionId]: currentText});
      setRewrittenTexts({...rewrittenTexts, [questionId]: response.data.rewritten_text});
      setSelectedVersions({...selectedVersions, [questionId]: 'rewritten'});
      toast.success('Response rewritten!');
    } catch (error) {
      toast.error('Failed to rewrite text');
    } finally {
      setRewritingQuestions({...rewritingQuestions, [questionId]: false});
    }
  };

  const handleVersionSelect = (questionId, version) => {
    setSelectedVersions({...selectedVersions, [questionId]: version});
    setAnswers({...answers, [questionId]: version === 'original' ? originalTexts[questionId] : rewrittenTexts[questionId]});
  };

  const handleSubmit = async () => {
    const requiredQuestions = survey.questions.filter(q => q.required);
    for (const q of requiredQuestions) {
      if (!answers[q.id]) { toast.error('Please answer all required questions'); return; }
    }
    try {
      const id = location.pathname.split('/')[2];
      const totalTime = surveyStartTime ? (Date.now() - surveyStartTime) / 1000 : 0;
      const isHastySurvey = totalTime < (survey.questions.length * surveySettings.hasty_survey_threshold);
      await axios.post(`${API}/surveys/${id}/submit`, {
        respondent_email: email || null, answers, original_text_answers: originalTexts,
        ai_rewritten_answers: rewrittenTexts, question_times: questionTimes,
        hasty_questions: hastyQuestions, idle_questions: idleQuestions,
        total_time: totalTime, is_hasty_survey: isHastySurvey
      });
      setSubmitted(true);
      toast.success('Response submitted!');
    } catch (error) {
      toast.error(`Submission failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (loading) return <div className="loading-spinner" data-testid="loading-spinner">Loading...</div>;

  if (!survey || survey.error === 'inactive') {
    return (
      <div className="take-survey-container" data-testid="inactive-survey">
        <Toaster position="top-right" richColors />
        <div className="survey-logo-container">
          <img src="/cxrecon-logo-new.png" alt="CXrecon Logo" className="survey-page-logo" data-testid="survey-logo" />
        </div>
        <div className="survey-header inactive-survey-notice">
          <h1 className="survey-title">Survey Currently Inactive</h1>
          <p className="survey-description">This survey is currently not accepting responses. Please contact the survey provider for more information.</p>
          <div className="inactive-icon">🚫</div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="take-survey-container" data-testid="thank-you-message">
        {survey?.branding && (survey.branding.company_name || survey.branding.company_logo_url) ? (
          <div className="survey-branded-header" data-testid="branded-header-thankyou">
            <div className="branded-header-left">
              {survey.branding.company_logo_url && <img src={survey.branding.company_logo_url} alt="Company Logo" className="branded-company-logo" />}
              {survey.branding.company_name && <span className="branded-company-name">{survey.branding.company_name}</span>}
            </div>
            <div className="branded-header-right">
              <span className="powered-by-label">powered by</span>
              <a href="https://cxrecon.com" target="_blank" rel="noopener noreferrer" className="logo-link" data-testid="cxrecon-logo-link">
                <img src="/cxrecon-logo-new.png" alt="CXrecon - Click to visit" className="powered-by-cxrecon-logo clickable" />
              </a>
            </div>
          </div>
        ) : (
          <div className="survey-logo-container">
            <a href="https://cxrecon.com" target="_blank" rel="noopener noreferrer" className="logo-link" data-testid="cxrecon-logo-link">
              <img src="/cxrecon-logo-new.png" alt="CXrecon Logo - Click to visit" className="survey-page-logo clickable" data-testid="survey-logo" />
            </a>
          </div>
        )}
        <div className="survey-header thank-you-header">
          <h1 className="survey-title">Thank You!</h1>
          <p className="survey-description">Your response has been recorded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="take-survey-container" data-testid="take-survey">
      <Toaster position="top-right" richColors />
      {survey.branding && (survey.branding.company_name || survey.branding.company_logo_url) ? (
        <div className="survey-branded-header" data-testid="branded-header">
          <div className="branded-header-left">
            {survey.branding.company_logo_url && <img src={survey.branding.company_logo_url} alt="Company Logo" className="branded-company-logo" data-testid="branded-company-logo" />}
            {survey.branding.company_name && <span className="branded-company-name" data-testid="branded-company-name">{survey.branding.company_name}</span>}
          </div>
          <div className="branded-header-right">
            <span className="powered-by-label">powered by</span>
            <a href="https://cxrecon.com" target="_blank" rel="noopener noreferrer" className="logo-link">
              <img src="/cxrecon-logo-new.png" alt="CXrecon - Click to visit" className="powered-by-cxrecon-logo clickable" data-testid="powered-by-logo" />
            </a>
          </div>
        </div>
      ) : (
        <div className="survey-logo-container">
          <a href="https://cxrecon.com" target="_blank" rel="noopener noreferrer" className="logo-link">
            <img src="/cxrecon-logo-new.png" alt="CXrecon Logo - Click to visit" className="survey-page-logo clickable" data-testid="survey-logo" />
          </a>
        </div>
      )}
      
      <div className="survey-header">
        <h1 className="survey-title" data-testid="survey-title">{survey.title}</h1>
        {survey.description && <p className="survey-description" data-testid="survey-description">{survey.description}</p>}
        {survey.metadata_collection_mode === 'none' && (
          <div className="privacy-notice privacy-anonymous" data-testid="privacy-anonymous">
            🔒 <strong>Fully Anonymous:</strong> This survey does not use cookies or collect any tracking data. Your responses are completely anonymous.
          </div>
        )}
        {survey.metadata_collection_mode === 'disclosed' && (
          <div className="privacy-notice privacy-disclosed" data-testid="privacy-disclosed">
            📋 <strong>Privacy Notice:</strong> This survey collects technical data (IP address, browser type, device type) for fraud prevention and analytics purposes. Your responses remain confidential and anonymous.
          </div>
        )}
      </div>

      <div className="question-card">
        <label className="form-label">Email (optional)</label>
        <input type="email" className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" />
      </div>

      {survey.questions.map((question, index) => (
        <div key={question.id} className="question-card" data-testid={`question-card-${index}`}>
          <div className="question-text" data-testid={`question-text-${index}`}>{question.question_text} {question.required && '*'}</div>

          {question.question_type === 'text' && (
            <div>
              <textarea className="form-textarea" value={answers[question.id] || ''} onChange={(e) => handleAnswerChange(question.id, e.target.value)} data-testid={`answer-input-${index}`} />
              {survey.allow_ai_assistance !== false && !rewrittenTexts[question.id] && (
                <button type="button" className="rewrite-btn" onClick={() => handleRewriteText(question.id, answers[question.id])}
                  disabled={rewritingQuestions[question.id] || !answers[question.id] || answers[question.id].trim().length < 3} data-testid={`rewrite-btn-${index}`}>
                  {rewritingQuestions[question.id] ? '✨ Rewriting...' : '✨ Rewrite My Above Response to this question with AI'}
                </button>
              )}
              {survey.allow_ai_assistance !== false && rewrittenTexts[question.id] && (
                <div className="rewrite-container">
                  <div className="selection-instruction">👇 Please select the radio button next to the response you would like to submit:</div>
                  <div className="version-option">
                    <label className="version-label">
                      <input type="radio" name={`version-${question.id}`} checked={selectedVersions[question.id] === 'original'} onChange={() => handleVersionSelect(question.id, 'original')} data-testid={`select-original-${index}`} />
                      <span className="version-title">Original Response</span>
                    </label>
                    <div className="version-text">{originalTexts[question.id]}</div>
                  </div>
                  <div className="version-option version-ai">
                    <label className="version-label">
                      <input type="radio" name={`version-${question.id}`} checked={selectedVersions[question.id] === 'rewritten'} onChange={() => handleVersionSelect(question.id, 'rewritten')} data-testid={`select-rewritten-${index}`} />
                      <span className="version-title">✨ AI-Enhanced Response</span>
                    </label>
                    <div className="version-text">{rewrittenTexts[question.id]}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {question.question_type === 'multiple_choice' && question.options && (
            <div>
              {question.options.map((option, i) => (
                <div key={i} style={{ marginBottom: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="radio" name={question.id} value={option} checked={answers[question.id] === option}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)} style={{ marginRight: '0.5rem' }} data-testid={`answer-option-${index}-${i}`} />
                    {option}
                  </label>
                </div>
              ))}
            </div>
          )}

          {(question.question_type === 'rating' || question.question_type === 'nps') && (
            <div className="rating-container">
              {Array.from({ length: question.question_type === 'nps' ? 11 : 5 }, (_, i) => (
                <button key={i} className={`rating-btn ${answers[question.id] === i.toString() ? 'selected' : ''}`}
                  onClick={() => handleAnswerChange(question.id, i.toString())} data-testid={`rating-button-${index}-${i}`}>{i}</button>
              ))}
            </div>
          )}
        </div>
      ))}

      <button className="submit-btn" onClick={handleSubmit} data-testid="submit-button">Submit Response</button>
    </div>
  );
};

export default TakeSurvey;
